import { Link } from 'react-router-dom'
import { CheckIcon } from '@heroicons/react/24/solid'
import type { PublicCourse } from '@/types/course'
import {
  COMPARE_COURSES,
  COMPARE_CRITERIA,
  findCoursesByCodes,
  courseLink,
} from '@/config/careerPaths'
import { Container, SectionHeading } from '@/components/ui'

interface EaseCompareTableProps {
  courses: PublicCourse[]
}

const EaseCompareTable = ({ courses }: EaseCompareTableProps) => {
  const resolvedCourses = COMPARE_COURSES.map((col) => {
    const matched = findCoursesByCodes(courses, col.courseCodes)
    return {
      ...col,
      href: matched[0] ? courseLink(matched[0]) : '/courses',
      resolved: Boolean(matched[0]),
    }
  })

  return (
    <section className="eh-section" id="compare">
      <Container>
        <div className="eh-reveal mb-8 md:mb-10">
          <SectionHeading
            eyebrow="Course compare"
            level="title"
            title="So sánh khóa học"
            description="Đối chiếu năng lực bạn muốn đạt được với các khóa phù hợp trong lộ trình M2MBA."
            align="center"
            className="mx-auto"
          />
        </div>

        {/* Desktop matrix */}
        <div className="eh-reveal hidden overflow-x-auto border border-eh-border bg-eh-cream md:block">
          <table className="w-full min-w-[900px] border-collapse text-left">
            <thead>
              <tr className="border-b border-eh-border bg-eh-forest-ink text-eh-cream">
                <th className="sticky left-0 z-[1] bg-eh-forest-ink p-4 text-eh-eyebrow font-semibold uppercase tracking-[0.08em]">
                  Bạn muốn
                </th>
                {resolvedCourses.map((col) => (
                  <th
                    key={col.id}
                    className="p-4 text-center text-eh-eyebrow font-semibold uppercase tracking-[0.06em]"
                  >
                    {col.resolved ? (
                      <Link to={col.href} className="hover:underline">
                        {col.name}
                      </Link>
                    ) : (
                      col.name
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-sm text-eh-charcoal">
              {COMPARE_CRITERIA.map((row, i) => (
                <tr
                  key={row.id}
                  className={`border-b border-eh-border ${
                    i % 2 === 1 ? 'bg-[#f8f8f4]' : ''
                  }`}
                >
                  <th
                    scope="row"
                    className="sticky left-0 z-[1] bg-inherit p-4 text-left font-semibold text-eh-forest-ink"
                  >
                    <span className="block">{row.title}</span>
                    {row.subtitle ? (
                      <span className="mt-1 block text-eh-caption font-normal">
                        {row.subtitle}
                      </span>
                    ) : null}
                  </th>
                  {resolvedCourses.map((col) => {
                    const hit = row.courseIds.includes(col.id)
                    return (
                      <td key={col.id} className="p-4 text-center">
                        {hit ? (
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-eh-sage text-eh-forest-ink">
                            <CheckIcon className="h-4 w-4" aria-label="Có" />
                          </span>
                        ) : (
                          <span className="text-eh-border" aria-hidden>
                            —
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: criteria list */}
        <div className="eh-reveal eh-divider-list md:hidden" role="list">
          {COMPARE_CRITERIA.map((row) => {
            const matchedCols = resolvedCourses.filter((col) =>
              row.courseIds.includes(col.id),
            )
            return (
              <article key={row.id} className="eh-divider-list__item" role="listitem">
                <h3 className="eh-heading">{row.title}</h3>
                {row.subtitle ? (
                  <p className="mt-1 text-eh-caption">{row.subtitle}</p>
                ) : null}
                <ul className="mt-3 space-y-2">
                  {matchedCols.map((col) => (
                    <li key={col.id}>
                      <Link
                        to={col.href}
                        className="inline-flex items-center gap-2 text-sm font-medium text-eh-forest-ink hover:underline"
                      >
                        <CheckIcon className="h-4 w-4 shrink-0" aria-hidden />
                        {col.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </article>
            )
          })}
        </div>
      </Container>
    </section>
  )
}

export default EaseCompareTable
